import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Upload, FileText, AlertCircle, CheckCircle, Image, FileImage, Lock, Eye, EyeOff } from 'lucide-react'
import { usePortfolioStore } from '../store/portfolioStore'
import { useTradingHistoryStore } from '../store/tradingHistoryStore'
import { useConfigStore } from '../store/configStore'
import Button from './ui/Button'
import Select from './ui/Select'
import toast from 'react-hot-toast'
import { apiFetch } from '../lib/api'

interface CsvImportModalProps {
  isOpen: boolean
  onClose: () => void
}

interface CsvRow {
  symbol: string
  shares: number
  purchase_price: number
  purchase_currency: 'USD' | 'THB'
  sector?: string
  project_id?: string
}


interface PreviewData {
  valid: CsvRow[]
  invalid: Array<{ row: number; data: any; errors: string[] }>
  total: number
}

interface StockSummary {
  bought: number
  sold: number
  avgPrice: number
  totalCost: number
}

interface TransactionData {
  transactions: any[]
  stockSummary: { [symbol: string]: StockSummary }
}

const CsvImportModal: React.FC<CsvImportModalProps> = ({ isOpen, onClose }) => {
  const { customPortfolios, loadCustomPortfolios, addHolding, assignHoldingToPortfolio, loadPortfolio } = usePortfolioStore()
  const { addTradingRecord } = useTradingHistoryStore()
  const { getConfigValue } = useConfigStore()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string>('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewData, setPreviewData] = useState<PreviewData | null>(null)
  const [transactionData, setTransactionData] = useState<TransactionData | null>(null)
  const [step, setStep] = useState<'select' | 'preview' | 'importing' | 'complete'>('select')
  const [csvFormat, setCsvFormat] = useState<'standard' | 'transactions'>('standard')
  const [fileType, setFileType] = useState<'csv' | 'image' | 'pdf'>('csv')
  const [processing, setProcessing] = useState(false)
  const [pdfPassword, setPdfPassword] = useState('')
  const [showPdfPassword, setShowPdfPassword] = useState(false)

  useEffect(() => {
    if (isOpen) {
      loadCustomPortfolios()
      // Reset state when modal opens
      setSelectedPortfolioId('')
      setSelectedFile(null)
      setPreviewData(null)
      setTransactionData(null)
      setStep('select')
      setCsvFormat('standard')
      setFileType('csv')
      setProcessing(false)
      setShowPdfPassword(false)
      // Pre-fill PDF password from saved config
      setPdfPassword(getConfigValue('pdf_password', ''))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const portfolioOptions = customPortfolios.map(portfolio => ({
    value: portfolio.id.toString(),
    label: portfolio.name,
    icon: portfolio.icon
  }))

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setSelectedFile(file)

    // Determine file type
    if (file.name.endsWith('.csv')) {
      setFileType('csv')
      const reader = new FileReader()
      reader.onload = (e) => {
        const text = e.target?.result as string
        parseAndPreviewCsv(text)
      }
      reader.readAsText(file)
    } else if (file.type.startsWith('image/')) {
      setFileType('image')
      await processImageFile(file)
    } else if (file.type === 'application/pdf') {
      setFileType('pdf')
      // Don't process immediately — let user confirm/change password first
    } else {
      toast.error('Please select a CSV, image, or PDF file')
      setSelectedFile(null)
      return
    }
  }

  // Process image file using backend API
  const processImageFile = async (file: File) => {
    setProcessing(true)
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await apiFetch('/api/process-image', { method: 'POST', body: formData })
      const resData = await response.json()

      if (resData.success) {
        // Convert parsed data to preview format
        const validRows: CsvRow[] = resData.parsedData.map((item: any) => ({
          symbol: item.symbol,
          shares: item.shares,
          purchase_price: item.purchase_price,
          purchase_currency: item.purchase_currency as 'USD' | 'THB',
          sector: item.sector || 'technology'
        }))

        const invalidRows = resData.errors.map((error: any) => ({
          row: error.line,
          data: { text: error.text },
          errors: [error.reason]
        }))

        setPreviewData({
          valid: validRows,
          invalid: invalidRows,
          total: resData.totalFound + resData.errors.length
        })
        setStep('preview')
        toast.success(`Extracted ${validRows.length} holdings from image`)
      }
    } catch (error) {
      console.error('Image processing error:', error)
      toast.error('Failed to process image. Please try again.')
    } finally {
      setProcessing(false)
    }
  }

  // Process PDF file using backend API
  const processPdfFile = async (file: File, password?: string) => {
    setProcessing(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      if (password) formData.append('password', password)

      const response = await apiFetch('/api/process-pdf', { method: 'POST', body: formData })
      const resData = await response.json()

      if (resData.success) {
        // Convert parsed data to preview format
        const validRows: CsvRow[] = resData.parsedData.map((item: any) => ({
          symbol: item.symbol,
          shares: item.shares,
          purchase_price: item.purchase_price,
          purchase_currency: item.purchase_currency as 'USD' | 'THB',
          sector: item.sector || 'technology'
        }))

        const invalidRows = resData.errors.map((error: any) => ({
          row: error.line,
          data: { text: error.text },
          errors: [error.reason]
        }))

        setPreviewData({
          valid: validRows,
          invalid: invalidRows,
          total: resData.totalFound + resData.errors.length
        })
        setStep('preview')
        toast.success(`Extracted ${validRows.length} holdings from PDF`)
      }
    } catch (error) {
      console.error('PDF processing error:', error)
      toast.error('Failed to process PDF. Please try again.')
    } finally {
      setProcessing(false)
    }
  }

  // Detect CSV format
  const detectCsvFormat = (csvText: string): 'standard' | 'transactions' => {
    const lines = csvText.trim().split('\n')
    if (lines.length < 2) return 'standard'
    
    // Check for TransactionsUS.csv or TransactionsTH.csv format indicators
    // Both formats have BUY, SELL, BALANCE in first line and Quantity, Cost/Unit in second line
    const firstLine = lines[0].toLowerCase()
    const secondLine = lines[1].toLowerCase()
    
    if ((firstLine.includes('buy') && firstLine.includes('sell') && firstLine.includes('balance')) ||
        (secondLine.includes('quantity') && secondLine.includes('cost/unit'))) {
      return 'transactions'
    }
    
    return 'standard'
  }

  // Parse TransactionsUS.csv and TransactionsTH.csv formats
  const parseTransactionsCsv = (csvText: string): TransactionData => {
    const lines = csvText.split('\n').filter(line => line.trim())
    const transactions: any[] = []
    const stockSummary: { [symbol: string]: StockSummary } = {}
    
    // Helper function to properly parse CSV with quoted values
    const parseCSVLine = (line: string): string[] => {
      const result = []
      let current = ''
      let inQuotes = false
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i]
        
        if (char === '"') {
          inQuotes = !inQuotes
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim())
          current = ''
        } else {
          current += char
        }
      }
      result.push(current.trim())
      return result
    }
    
    // Detect if this is Thai format by checking for Thai Baht symbol
    const isThaiFormat = csvText.includes('฿') || csvText.includes('🇹🇭')
    const normalizeTradeDate = (value?: string | null) => {
      if (!value) return new Date().toISOString()
      const trimmed = value.trim()
      if (!trimmed) return new Date().toISOString()
      if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
        return `${trimmed.slice(0, 10)}T00:00:00.000Z`
      }
      const dmyMatch = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/)
      if (dmyMatch) {
        const [, day, month, year] = dmyMatch
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00.000Z`
      }
      const parsed = new Date(trimmed)
      return Number.isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString()
    }
    
    // Skip header lines (first 2 lines for Thai format, 3 for US format)
    const startLine = isThaiFormat ? 2 : 3
    
    for (let i = startLine; i < lines.length; i++) {
      const fields = parseCSVLine(lines[i])
      
      // Skip empty lines
      if (!fields[0] || fields[0].trim() === '') continue
      
      let symbol, broker, buyDate, buyQty, buyPrice, buyFees, buyValue, sellDate, sellQty, sellPrice, sellFees, sellValue, currency
      
      if (isThaiFormat) {
        // Thai format: symbol, flag, broker, type, date, qty, price, fees, value, ...
        [symbol, , broker, , buyDate, buyQty, buyPrice, buyFees, buyValue] = fields
        currency = 'THB'
        
        // No sell data in Thai format (only buy transactions)
        sellDate = sellQty = sellPrice = sellFees = sellValue = null
      } else {
        // US format: symbol, broker, ..., buyQty, buyPrice, buyFees, buyValue, sellDate, sellQty, sellPrice, sellFees, sellValue
        [symbol, , broker, , buyDate, buyQty, buyPrice, buyFees, buyValue, sellDate, sellQty, sellPrice, sellFees, sellValue] = fields
        currency = 'USD'
      }
      
      // Clean up values - handle Thai Baht symbol and comma separators
      const cleanNumber = (str: string) => {
        if (!str) return 0
        // Remove Thai Baht symbol, dollar sign, quotes, commas, and spaces
        return parseFloat(str.replace(/[$฿,"\s]/g, '')) || 0
      }
      
      const symbolTrimmed = symbol.trim()
      
      // Initialize stock summary if not exists
      if (!stockSummary[symbolTrimmed]) {
        stockSummary[symbolTrimmed] = { bought: 0, sold: 0, avgPrice: 0, totalCost: 0 }
      }
      
      // Add buy transaction if data exists
      if (buyQty && buyPrice && buyValue) {
        const shares = cleanNumber(buyQty)
        const price = cleanNumber(buyPrice)
        const totalAmount = cleanNumber(buyValue)
        
        transactions.push({
          symbol: symbolTrimmed,
          action: 'BUY',
          shares,
          price,
          total_amount: totalAmount,
          fees: cleanNumber(buyFees),
          currency,
          created_at: normalizeTradeDate(buyDate),
          notes: `Imported from CSV - ${broker}`
        })
        
        // Update stock summary for remaining shares calculation
        stockSummary[symbolTrimmed].bought += shares
        stockSummary[symbolTrimmed].totalCost += totalAmount
      }
      
      // Add sell transaction if data exists (only for US format)
      if (!isThaiFormat && sellQty && sellPrice && sellValue) {
        const shares = cleanNumber(sellQty)
        
        transactions.push({
          symbol: symbolTrimmed,
          action: 'SELL',
          shares,
          price: cleanNumber(sellPrice),
          total_amount: cleanNumber(sellValue),
          fees: cleanNumber(sellFees || '0'),
          currency,
          created_at: normalizeTradeDate(sellDate),
          notes: `Imported from CSV - ${broker}`
        })
        
        // Update stock summary for remaining shares calculation
        stockSummary[symbolTrimmed].sold += shares
      }
    }
    
    // Calculate average price for remaining shares
    Object.keys(stockSummary).forEach(symbol => {
      const summary = stockSummary[symbol]
      if (summary.bought > 0) {
        summary.avgPrice = summary.totalCost / summary.bought
      }
    })
    
    return { transactions, stockSummary }
  }

  const parseAndPreviewCsv = (csvText: string) => {
    try {
      // Detect format
      const format = detectCsvFormat(csvText)
      setCsvFormat(format)
      
      if (format === 'transactions') {
        // Parse TransactionsUS.csv format
        const { transactions, stockSummary } = parseTransactionsCsv(csvText)
        
        // Store transaction data for later use in import
        setTransactionData({ transactions, stockSummary })
        
        // Convert stockSummary to preview format (only stocks with remaining shares > 0)
        const validRows: CsvRow[] = []
        // Detect currency from the CSV content
        const isThaiFormat = csvText.includes('฿') || csvText.includes('🇹🇭')
        const defaultCurrency = isThaiFormat ? 'THB' : 'USD'
        
        Object.entries(stockSummary).forEach(([symbol, summary]) => {
          const remainingShares = summary.bought - summary.sold
          if (remainingShares > 0) {
            validRows.push({
              symbol: symbol,
              shares: remainingShares,
              purchase_price: summary.avgPrice,
              purchase_currency: defaultCurrency,
              sector: 'technology' // Default sector
            })
          }
        })
        
        setPreviewData({
          valid: validRows,
          invalid: [],
          total: transactions.length // Show total transactions count
        })
        setStep('preview')
        return
      }
      
      // Standard CSV format parsing
      const lines = csvText.trim().split('\n')
      const headers = lines[0].toLowerCase().split(',').map(h => h.trim())
      
      // Expected headers
      const requiredHeaders = ['symbol', 'shares', 'purchase_price']
      
      // Check if required headers exist
      const missingHeaders = requiredHeaders.filter(h => !headers.includes(h))
      if (missingHeaders.length > 0) {
        toast.error(`Missing required headers: ${missingHeaders.join(', ')}`)
        return
      }

      const validRows: CsvRow[] = []
      const invalidRows: Array<{ row: number; data: any; errors: string[] }> = []

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim())
        const rowData: any = {}
        const errors: string[] = []

        // Map values to headers
        headers.forEach((header, index) => {
          rowData[header] = values[index] || ''
        })

        // Validate required fields
        if (!rowData.symbol) {
          errors.push('Symbol is required')
        }

        const shares = parseFloat(rowData.shares)
        if (isNaN(shares) || shares <= 0) {
          errors.push('Shares must be a positive number')
        }

        const price = parseFloat(rowData.purchase_price)
        if (isNaN(price) || price <= 0) {
          errors.push('Purchase price must be a positive number')
        }

        // Validate currency
        const currency = rowData.purchase_currency?.toUpperCase()
        if (currency && !['USD', 'THB'].includes(currency)) {
          errors.push('Purchase currency must be USD or THB')
        }

        if (errors.length > 0) {
          invalidRows.push({ row: i + 1, data: rowData, errors })
        } else {
          validRows.push({
            symbol: rowData.symbol.toUpperCase(),
            shares: shares,
            purchase_price: price,
            purchase_currency: (currency || 'THB') as 'USD' | 'THB',
            sector: rowData.sector || '',
            project_id: rowData.project_id || ''
          })
        }
      }

      setPreviewData({
        valid: validRows,
        invalid: invalidRows,
        total: lines.length - 1
      })
      setStep('preview')

    } catch (error) {
      console.error('Error parsing CSV:', error)
      toast.error('Error parsing CSV file')
    }
  }

  const handleImport = async () => {
    if (!previewData || !selectedPortfolioId) return

    setStep('importing')

    const selectedPortfolio = customPortfolios.find(p => p.id.toString() === selectedPortfolioId)
    if (!selectedPortfolio) {
      toast.error('Selected portfolio not found')
      return
    }

    try {
      let successCount = 0
      let failCount = 0
      
      // Determine port_api_type based on portfolio
      let port_api_type: 'us_stock' | 'th_stock' | 'th_fund' | 'us_fund' | 'gold'
      
      if (selectedPortfolio.name.includes('กองทุนไทย')) {
        port_api_type = 'th_fund'
      } else if (selectedPortfolio.name.includes('กองทุนต่างประเทศ')) {
        port_api_type = 'us_fund'
      } else if (selectedPortfolio.name.includes('ทองคำ')) {
        port_api_type = 'gold'
      } else {
        port_api_type = selectedPortfolio.currency === 'USD' ? 'us_stock' : 'th_stock'
      }

      if (csvFormat === 'transactions' && transactionData) {
        // Import transactions first
        toast.loading('Importing transactions...', { id: 'import-transactions' })
        
        for (const transaction of transactionData.transactions) {
          try {
            await addTradingRecord({
              symbol: transaction.symbol,
              name: transaction.symbol,
              port_api_type: port_api_type,
              action: transaction.action,
              shares: transaction.shares,
              price: transaction.price,
              currency: transaction.currency,
              total_amount: transaction.total_amount,
              fees: transaction.fees,
              notes: transaction.notes,
              created_at: transaction.created_at,
              portfolio_id: parseInt(selectedPortfolioId)
            })
            successCount++
          } catch (error) {
            console.error('Error importing transaction:', error)
            failCount++
          }
        }
        
        toast.dismiss('import-transactions')
        toast.loading('Adding remaining shares to portfolio...', { id: 'import-holdings' })
        
        // Add remaining shares to portfolio
        let portfolioAddCount = 0
        // Detect currency from first transaction
        const firstTransaction = transactionData.transactions[0]
        const portfolioCurrency = firstTransaction?.currency || 'USD'
        
        for (const [symbol, summary] of Object.entries(transactionData.stockSummary)) {
          const remainingShares = summary.bought - summary.sold
          if (remainingShares > 0) {
            try {
              const result = await addHolding({
                symbol,
                name: symbol,
                port_api_type: port_api_type,
                shares: remainingShares,
                purchase_price: summary.avgPrice,
                purchase_currency: portfolioCurrency,
                recordTradingHistory: false,
                portfolio_id: parseInt(selectedPortfolioId)
              })
              
              if (result.id) {
                await assignHoldingToPortfolio(parseInt(selectedPortfolioId), result.id)
              }
              
              portfolioAddCount++
            } catch (error) {
              console.error(`Error adding ${symbol} to portfolio:`, error)
            }
          }
        }
        
        toast.dismiss('import-holdings')
        
        if (successCount > 0) {
          let message = `Successfully imported ${successCount} transactions!`
          if (portfolioAddCount > 0) {
            message += ` Added ${portfolioAddCount} stocks to portfolio.`
          }
          toast.success(message)
        }
        
      } else {
        // Standard CSV import (direct holdings)
        for (const row of previewData.valid) {
          try {
            // Add holding
            const result = await addHolding({
              symbol: row.symbol,
              name: row.symbol,
              port_api_type: port_api_type,
              sector: row.sector,
              shares: row.shares,
              purchase_price: row.purchase_price,
              purchase_currency: row.purchase_currency,
              portfolio_id: parseInt(selectedPortfolioId),
              project_id: (port_api_type === 'th_fund' && row.project_id) ? row.project_id : undefined
            })

            // Assign to portfolio
            if (result.id) {
              await assignHoldingToPortfolio(parseInt(selectedPortfolioId), result.id)
            }

            successCount++
          } catch (error) {
            console.error('Error importing row:', error)
            failCount++
          }
        }
        
        if (failCount === 0) {
          toast.success(`Successfully imported ${successCount} holdings`)
        } else {
          toast(`Imported ${successCount} holdings, ${failCount} failed`, { icon: '⚠️' })
        }
      }

      // Reload portfolio data
      await loadPortfolio()

      setStep('complete')
    } catch (error) {
      console.error('Error during import:', error)
      toast.error('Import failed')
    } finally {
      // Step is already set to 'complete' in the try block
    }
  }

  const resetModal = () => {
    setSelectedPortfolioId('')
    setSelectedFile(null)
    setPreviewData(null)
    setTransactionData(null)
    setStep('select')
    setCsvFormat('standard')
    setFileType('csv')
    setProcessing(false)
    setShowPdfPassword(false)
    setPdfPassword(getConfigValue('pdf_password', ''))
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleClose = () => {
    resetModal()
    onClose()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="sheet fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && handleClose()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Import Data to Portfolio
              </h2>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {step === 'select' && (
              <div className="space-y-4">
                {/* Portfolio Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Portfolio to Import to
                  </label>
                  <Select
                    value={selectedPortfolioId}
                    options={portfolioOptions}
                    onChange={(value) => setSelectedPortfolioId(value)}
                    placeholder="Choose a portfolio..."
                  />
                </div>

                {/* File Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select File (CSV, Image, or PDF)
                  </label>
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                    {processing ? (
                      <div className="flex flex-col items-center">
                        <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full mb-2"></div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Processing {fileType}...</p>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-center space-x-2 mb-2">
                          <FileText className="w-8 h-8 text-gray-400" />
                          <Image className="w-8 h-8 text-gray-400" />
                          <FileImage className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          Drag & drop your file here, or click to browse
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                          Supports CSV, Images (JPG, PNG, WebP) & PDF files
                        </p>
                      </>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.jpg,.jpeg,.png,.gif,.webp,.pdf"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <Button variant="secondary"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Choose File
                    </Button>
                    {selectedFile && (
                      <p className="text-sm text-gain mt-2">
                        Selected: {selectedFile.name}
                      </p>
                    )}
                  </div>
                </div>

                {/* PDF Password field — shown only when PDF is selected */}
                {fileType === 'pdf' && selectedFile && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-4 space-y-3"
                  >
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                      <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
                        PDF Password (ถ้ามี)
                      </span>
                      {pdfPassword && (
                        <span className="text-xs text-amber-600 dark:text-amber-400 ml-auto">
                          pre-filled จาก Settings
                        </span>
                      )}
                    </div>
                    <div className="relative">
                      <input
                        type={showPdfPassword ? 'text' : 'password'}
                        value={pdfPassword}
                        onChange={(e) => setPdfPassword(e.target.value)}
                        placeholder="ไม่มี password ปล่อยว่างไว้"
                        className="w-full px-4 py-2 pr-10 border border-amber-300 dark:border-amber-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPdfPassword(!showPdfPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        {showPdfPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <Button variant="secondary" className="w-full"
                      onClick={() => processPdfFile(selectedFile, pdfPassword || undefined)}
                      disabled={processing}
                     
                    >
                      {processing ? 'Processing PDF...' : 'Process PDF'}
                    </Button>
                  </motion.div>
                )}

                {/* Format Info */}
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Supported File Formats:</h3>
                  <div className="text-sm text-blue-700 dark:text-blue-300 space-y-3">
                    <div>
                      <p><strong>1. CSV Files:</strong></p>
                      <p>Standard format: symbol, shares, purchase_price, purchase_currency, sector</p>
                      <p>Transaction format: @TransactionsUS.csv / @TransactionsTH.csv</p>
                    </div>
                    <div>
                      <p><strong>2. Image Files (JPG, PNG, WebP):</strong></p>
                      <p>Uses OCR to extract portfolio data from screenshots or photos</p>
                      <p>Supports formats like: "AAPL 100 shares $150.50 USD"</p>
                    </div>
                    <div>
                      <p><strong>3. PDF Files:</strong></p>
                      <p>Extracts text from PDF brokerage statements or reports</p>
                      <p>Automatically parses stock symbols, quantities, and prices</p>
                    </div>
                    <div className="text-xs">
                      <p><strong>Note:</strong> Image and PDF processing uses AI to extract data. Please review the preview before importing.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 'preview' && previewData && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-900 dark:text-white">Preview Import Data</h3>
                  <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <div>Format: <span className="font-medium">
                      {fileType === 'csv' 
                        ? (csvFormat === 'transactions' ? '@TransactionsUS.csv' : 'Standard CSV')
                        : fileType === 'image' 
                        ? 'Image (OCR)' 
                        : 'PDF (Text Extraction)'
                      }
                    </span></div>
                    <div>Portfolio: <span className="font-medium">{customPortfolios.find(p => p.id.toString() === selectedPortfolioId)?.name}</span></div>
                  </div>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg text-center">
                    <div className="text-2xl font-bold text-gain">{previewData.valid.length}</div>
                    <div className="text-sm text-green-700 dark:text-green-300">Valid Rows</div>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg text-center">
                    <div className="text-2xl font-bold text-loss">{previewData.invalid.length}</div>
                    <div className="text-sm text-red-700 dark:text-red-300">Invalid Rows</div>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-center">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{previewData.total}</div>
                    <div className="text-sm text-blue-700 dark:text-blue-300">Total Rows</div>
                  </div>
                </div>

                {/* Valid Data Preview */}
                {previewData.valid.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-gain" />
                      Valid Data (First 5 rows)
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border border-gray-200 dark:border-gray-700 rounded-lg">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-3 py-2 text-left">Symbol</th>
                            <th className="px-3 py-2 text-left">Shares</th>
                            <th className="px-3 py-2 text-left">Price</th>
                            <th className="px-3 py-2 text-left">Currency</th>
                            <th className="px-3 py-2 text-left">Sector</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.valid.slice(0, 5).map((row, index) => (
                            <tr key={index} className="border-t border-gray-200 dark:border-gray-700">
                              <td className="px-3 py-2 font-mono">{row.symbol}</td>
                              <td className="px-3 py-2">{row.shares}</td>
                              <td className="px-3 py-2">{row.purchase_price}</td>
                              <td className="px-3 py-2">{row.purchase_currency}</td>
                              <td className="px-3 py-2">{row.sector || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Invalid Data */}
                {previewData.invalid.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-loss" />
                      Invalid Data
                    </h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {previewData.invalid.map((item, index) => (
                        <div key={index} className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                          <div className="text-sm font-medium text-red-700 dark:text-red-300">
                            Row {item.row}: {item.errors.join(', ')}
                          </div>
                          <div className="text-xs text-loss mt-1">
                            Data: {JSON.stringify(item.data)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button variant="secondary" className="flex-1"
                    onClick={() => setStep('select')}
                   
                  >
                    Back
                  </Button>
                  <Button className="flex-1"
                    onClick={handleImport}
                    disabled={previewData.valid.length === 0 || !selectedPortfolioId}
                  >
                    Import {previewData.valid.length} Holdings
                  </Button>
                </div>
              </div>
            )}

            {step === 'importing' && (
              <div className="text-center py-8">
                <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-400">Importing holdings...</p>
              </div>
            )}

            {step === 'complete' && (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-gain mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Import Complete!</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Your holdings have been successfully imported to the selected portfolio.
                </p>
                <Button onClick={handleClose}>
                  Close
                </Button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default CsvImportModal
